import { v4 as uuidv4 } from 'uuid';
import { TextOverlay } from '../types';

/**
 * Curated "text frame" templates — ready-made, styled text scenes the user can drop
 * onto the canvas instead of hand-building overlays. Each template composes existing
 * `TextOverlay` primitives (design + background + animation in/out + position + font),
 * so the editor previews them live and the assembly pipeline burns them in verbatim via
 * the existing `text_overlays` path. No backend/render change required.
 *
 * Scope note: these stay within the overlay engine — rich text styling, background
 * bands, and entry/exit animation, but NOT full-frame video backgrounds that cover the
 * avatar (that needs the Creatomate-authored template path). Designed to cover the
 * common social-video text needs: titles, hooks, lower-thirds, quotes, CTAs, lists.
 */

export type TextTemplateCategory =
  | 'title' | 'hook' | 'lower-third' | 'quote' | 'cta' | 'list' | 'animated';

export interface TextFrameTemplate {
  id: string;
  label: string;
  category: TextTemplateCategory;
  /** Build the overlay(s) for this template, starting at `atTimeSec`. */
  build: (atTimeSec: number) => TextOverlay[];
  /** Lightweight styling so the gallery card resembles the result. */
  swatch: {
    text: string;
    color: string;
    background?: string;
    italic?: boolean;
    stroke?: string;
    gradient?: boolean;
  };
}

const D = 4; // default on-screen duration (s)

/** Single styled overlay with sensible defaults. */
const ov = (o: Partial<TextOverlay> & { text: string; time: number }): TextOverlay => ({
  id: uuidv4(),
  duration: D,
  style: 'subtitle',
  position: 'center',
  x_pct: 50,
  y_pct: 50,
  animation: 'fade',
  animation_out: 'fade',
  ...o,
});

export const TEXT_FRAME_TEMPLATES: TextFrameTemplate[] = [
  // ── Titles ──────────────────────────────────────────────
  {
    id: 'title-bold-pop', label: 'Bold Title', category: 'title',
    swatch: { text: 'YOUR TITLE', color: '#ffe81a', background: '#111' },
    build: (t) => [ov({ text: 'YOUR TITLE', time: t, design: 'bold', y_pct: 46, fontSize: 11, width: 84, bold: true, animation: 'pop', anim_duration: 0.55, animation_out: 'fade' })],
  },
  {
    id: 'title-cinema', label: 'Cinematic', category: 'title',
    swatch: { text: 'The Story Begins', color: '#fff', background: '#0b0b0b', stroke: '#000' },
    build: (t) => [ov({ text: 'The Story Begins', time: t, design: 'cinema', y_pct: 50, fontSize: 9, width: 82, animation: 'slide-up', animation_out: 'fade', duration: 5 })],
  },
  {
    id: 'title-gradient', label: 'Gradient', category: 'title',
    swatch: { text: 'Make It Pop', color: '#ff7a18', background: '#1f2937', gradient: true },
    build: (t) => [ov({ text: 'Make It Pop', time: t, design: 'gradient', y_pct: 48, fontSize: 12, width: 84, bold: true, animation: 'slide-up', animation_out: 'fade' })],
  },
  {
    id: 'title-band', label: 'Title Band', category: 'title',
    swatch: { text: 'HEADLINE', color: '#111', background: '#FFE100' },
    build: (t) => [ov({ text: 'HEADLINE', time: t, design: 'classic', color: '#111', y_pct: 46, fontSize: 9, width: 86, bold: true, backgroundColor: '#FFE100', animation: 'wipe-down', animation_out: 'fade' })],
  },
  {
    id: 'title-subtitle-stack', label: 'Title + Subtitle', category: 'title',
    swatch: { text: 'BIG IDEA\na supporting line', color: '#fff', background: '#1f2937' },
    // Two distinct designs grouped as one element (move/select/delete together).
    build: (t) => { const g = uuidv4(); return [
      ov({ groupId: g, text: 'BIG IDEA', time: t, design: 'bold', y_pct: 47, fontSize: 10, width: 84, bold: true, animation: 'slide-up', animation_out: 'fade' }),
      ov({ groupId: g, text: 'a short supporting line', time: t, design: 'minimalist', color: '#e4b372', y_pct: 55, fontSize: 4.5, width: 74, animation: 'fade', anim_duration: 0.7, animation_out: 'fade' }),
    ]; },
  },
  {
    id: 'combo-promo', label: 'Promo Combo', category: 'title',
    swatch: { text: 'NEW DROP\njust launched\nShop now →', color: '#ffe81a', background: '#111' },
    // Three designs in one group: bold headline + accent subtitle + highlighted CTA.
    build: (t) => { const g = uuidv4(); return [
      ov({ groupId: g, text: 'NEW DROP', time: t, design: 'bold', y_pct: 43, fontSize: 11, width: 82, bold: true, animation: 'pop', anim_duration: 0.55, animation_out: 'fade' }),
      ov({ groupId: g, text: 'just launched', time: t, design: 'minimalist', color: '#e4b372', y_pct: 51, fontSize: 5, width: 70, animation: 'fade', anim_duration: 0.7, animation_out: 'fade' }),
      ov({ groupId: g, text: 'Shop now →', time: t, design: 'highlight', y_pct: 60, fontSize: 6.5, width: 52, bold: true, animation: 'bounce', anim_duration: 0.6, animation_out: 'pop' }),
    ]; },
  },

  // ── Hooks (scroll-stoppers) ─────────────────────────────
  {
    id: 'hook-pov', label: 'POV', category: 'hook',
    swatch: { text: 'POV: you just…', color: '#fff', background: '#000' },
    build: (t) => [ov({ text: 'POV: you just found the hack', time: t, design: 'shadow', position: 'top', y_pct: 20, fontSize: 7, width: 82, bold: true, animation: 'slide-down', animation_out: 'fade' })],
  },
  {
    id: 'hook-didyouknow', label: 'Did you know?', category: 'hook',
    swatch: { text: 'Did you know?', color: '#39ff14' },
    build: (t) => [ov({ text: 'Did you know?', time: t, design: 'neon', y_pct: 40, fontSize: 9, width: 80, bold: true, animation: 'pop', animation_out: 'fade' })],
  },
  {
    id: 'hook-wait', label: 'Wait for it', category: 'hook',
    swatch: { text: 'Wait for it…', color: '#fff', italic: true, background: '#111' },
    build: (t) => [ov({ text: 'Wait for it…', time: t, design: 'minimalist', y_pct: 50, fontSize: 8, width: 70, italic: true, animation: 'typewriter', anim_duration: 0.8, animation_out: 'fade' })],
  },
  {
    id: 'hook-sign', label: 'This is your sign', category: 'hook',
    swatch: { text: 'This is your sign', color: '#fff', background: 'rgba(99,102,241,0.92)' },
    build: (t) => [ov({ text: 'This is your sign', time: t, design: 'classic', y_pct: 46, fontSize: 8, width: 80, bold: true, backgroundColor: 'rgba(99,102,241,0.92)', animation: 'pop', animation_out: 'fade' })],
  },

  // ── Lower thirds ────────────────────────────────────────
  {
    id: 'lower-third-pill', label: 'Pill Lower Third', category: 'lower-third',
    swatch: { text: 'Name · Role', color: '#fff', background: 'rgba(15,23,42,0.92)' },
    build: (t) => [ov({ text: 'Name · Role', time: t, design: 'pill', position: 'bottom', y_pct: 82, fontSize: 5, width: 60, backgroundColor: 'rgba(15,23,42,0.92)', animation: 'slide-right', animation_out: 'fade' })],
  },
  {
    id: 'lower-third-bar', label: 'Caption Bar', category: 'lower-third',
    swatch: { text: 'Key takeaway here', color: '#fff', background: 'rgba(0,0,0,0.85)' },
    build: (t) => [ov({ text: 'Key takeaway here', time: t, design: 'classic', position: 'bottom', y_pct: 84, fontSize: 5.5, width: 80, backgroundColor: 'rgba(0,0,0,0.85)', animation: 'slide-up', animation_out: 'fade' })],
  },
  {
    id: 'lower-third-name-tag', label: 'Name Tag', category: 'lower-third',
    swatch: { text: 'JORDAN LEE\nFounder, DaisyAI', color: '#fff', background: 'rgba(0,0,0,0.85)' },
    build: (t) => { const g = uuidv4(); return [
      ov({ groupId: g, text: 'JORDAN LEE', time: t, design: 'classic', color: '#111', position: 'bottom', y_pct: 80, fontSize: 5.2, width: 54, bold: true, backgroundColor: 'rgba(255,255,255,0.96)', animation: 'slide-right', animation_out: 'slide-left' }),
      ov({ groupId: g, text: 'Founder, DaisyAI', time: t, design: 'classic', position: 'bottom', y_pct: 87, fontSize: 3.6, width: 54, backgroundColor: 'rgba(47,73,65,0.96)', animation: 'slide-right', anim_duration: 0.5, animation_out: 'slide-left' }),
    ]; },
  },
  {
    id: 'lower-third-location', label: 'Location', category: 'lower-third',
    swatch: { text: '📍 New York', color: '#fff', background: 'rgba(0,0,0,0.7)' },
    build: (t) => [ov({ text: '📍 New York', time: t, design: 'pill', position: 'top', y_pct: 14, fontSize: 4.5, width: 40, backgroundColor: 'rgba(0,0,0,0.7)', animation: 'slide-down', animation_out: 'fade' })],
  },

  // ── Quote ───────────────────────────────────────────────
  {
    id: 'quote-clean', label: 'Quote Card', category: 'quote',
    swatch: { text: '“Your quote…”', color: '#fff', italic: true },
    build: (t) => [ov({ text: '“Your quote goes here.”', time: t, design: 'minimalist', y_pct: 46, fontSize: 7, width: 80, italic: true, animation: 'fade', anim_duration: 0.6, animation_out: 'fade', duration: 5 })],
  },
  {
    id: 'quote-attribution', label: 'Quote + Author', category: 'quote',
    swatch: { text: '“Stay hungry.”\n— Steve Jobs', color: '#fff', italic: true, background: '#111' },
    build: (t) => { const g = uuidv4(); return [
      ov({ groupId: g, text: '“Stay hungry, stay foolish.”', time: t, design: 'cinema', y_pct: 48, fontSize: 6.5, width: 82, italic: true, animation: 'fade', anim_duration: 0.6, animation_out: 'fade', duration: 5 }),
      ov({ groupId: g, text: '— Steve Jobs', time: t, design: 'minimalist', color: '#e4b372', y_pct: 57, fontSize: 4, width: 60, animation: 'fade', anim_duration: 0.9, animation_out: 'fade', duration: 5 }),
    ]; },
  },

  // ── CTA ─────────────────────────────────────────────────
  {
    id: 'cta-follow', label: 'Follow CTA', category: 'cta',
    swatch: { text: 'Follow for more →', color: '#FFE100', background: '#111', stroke: '#000' },
    build: (t) => [ov({ text: 'Follow for more →', time: t, design: 'highlight', position: 'bottom', y_pct: 80, fontSize: 6.5, width: 72, bold: true, animation: 'bounce', anim_duration: 0.6, animation_out: 'pop' })],
  },
  {
    id: 'cta-link-bio', label: 'Link in Bio', category: 'cta',
    swatch: { text: 'Link in bio', color: '#39ff14' },
    build: (t) => [ov({ text: 'Link in bio', time: t, design: 'neon', y_pct: 70, fontSize: 8, width: 70, bold: true, animation: 'pop', animation_out: 'fade' })],
  },
  {
    id: 'cta-subscribe', label: 'Subscribe', category: 'cta',
    swatch: { text: 'SUBSCRIBE', color: '#fff', background: 'rgba(220,38,38,0.95)' },
    build: (t) => [ov({ text: 'SUBSCRIBE', time: t, design: 'classic', y_pct: 72, fontSize: 8, width: 64, bold: true, backgroundColor: 'rgba(220,38,38,0.95)', animation: 'pop', anim_duration: 0.5, animation_out: 'bounce' })],
  },
  {
    id: 'cta-swipe', label: 'Swipe Up', category: 'cta',
    swatch: { text: '↑ Swipe up', color: '#fff', background: '#111' },
    build: (t) => [ov({ text: '↑ Swipe up', time: t, design: 'shadow', position: 'bottom', y_pct: 78, fontSize: 7, width: 60, bold: true, animation: 'slide-up', animation_out: 'slide-up' })],
  },
  {
    id: 'cta-sale', label: 'Sale Badge', category: 'cta',
    swatch: { text: '50% OFF', color: '#111', background: '#FFE100' },
    build: (t) => [ov({ text: '50% OFF', time: t, design: 'classic', color: '#111', y_pct: 30, fontSize: 10, width: 48, bold: true, backgroundColor: '#FFE100', rotation: 350, animation: 'spin', animation_out: 'pop' })],
  },

  // ── Lists / steps ───────────────────────────────────────
  {
    id: 'list-step', label: 'Step Number', category: 'list',
    swatch: { text: 'STEP 1\nDescribe the step', color: '#111', background: '#FFE100' },
    build: (t) => { const g = uuidv4(); return [
      ov({ groupId: g, text: 'STEP 1', time: t, design: 'classic', color: '#111', position: 'top', y_pct: 20, fontSize: 4.8, width: 30, bold: true, backgroundColor: '#FFE100', animation: 'slide-right', animation_out: 'fade' }),
      ov({ groupId: g, text: 'Describe the step here', time: t, design: 'classic', position: 'top', y_pct: 30, fontSize: 6, width: 80, bold: true, backgroundColor: 'rgba(0,0,0,0.82)', animation: 'slide-up', anim_duration: 0.5, animation_out: 'fade' }),
    ]; },
  },
  {
    id: 'list-number', label: 'Big Number', category: 'list',
    swatch: { text: '01\nYour point', color: '#ff7a18', background: '#111', gradient: true },
    build: (t) => { const g = uuidv4(); return [
      ov({ groupId: g, text: '01', time: t, design: 'gradient', y_pct: 45, fontSize: 14, width: 30, bold: true, animation: 'pop', animation_out: 'fade' }),
      ov({ groupId: g, text: 'Your point', time: t, design: 'classic', y_pct: 55, fontSize: 6, width: 76, bold: true, animation: 'slide-up', anim_duration: 0.5, animation_out: 'fade' }),
    ]; },
  },
  {
    id: 'list-tip', label: 'Pro Tip', category: 'list',
    swatch: { text: '💡 Pro tip', color: '#111', background: '#FFE100' },
    build: (t) => [ov({ text: '💡 Pro tip', time: t, design: 'classic', color: '#111', position: 'top', y_pct: 18, fontSize: 5.5, width: 44, bold: true, backgroundColor: '#FFE100', animation: 'slide-down', animation_out: 'fade' })],
  },

  // ── Animated ────────────────────────────────────────────
  {
    id: 'animated-typewriter', label: 'Typewriter', category: 'animated',
    swatch: { text: 'Typing in…', color: '#fff', background: '#111' },
    build: (t) => [ov({ text: 'Typing in…', time: t, design: 'classic', y_pct: 50, fontSize: 7.5, width: 80, animation: 'typewriter', anim_duration: 0.9, animation_out: 'fade' })],
  },
  {
    id: 'animated-fire', label: 'Fire Word', category: 'animated',
    swatch: { text: 'HOT 🔥', color: '#FF6B00', background: '#111' },
    build: (t) => [ov({ text: 'HOT 🔥', time: t, design: 'fire', y_pct: 46, fontSize: 12, width: 60, bold: true, animation: 'pop', anim_duration: 0.55, animation_out: 'fade' })],
  },
  {
    id: 'animated-spin', label: 'Spin In', category: 'animated',
    swatch: { text: 'New!', color: '#ffe81a', background: '#111' },
    build: (t) => [ov({ text: 'New!', time: t, design: 'bold', y_pct: 46, fontSize: 12, width: 50, bold: true, animation: 'spin', animation_out: 'pop' })],
  },
  {
    id: 'animated-bounce', label: 'Bounce', category: 'animated',
    swatch: { text: 'LET’S GO', color: '#fff', background: 'rgba(99,102,241,0.95)' },
    build: (t) => [ov({ text: 'LET’S GO', time: t, design: 'classic', y_pct: 48, fontSize: 10, width: 64, bold: true, backgroundColor: 'rgba(99,102,241,0.95)', animation: 'bounce', anim_duration: 0.6, animation_out: 'pop' })],
  },
  {
    id: 'animated-wipe', label: 'Wipe Reveal', category: 'animated',
    swatch: { text: 'REVEAL', color: '#111', background: '#fff' },
    build: (t) => [ov({ text: 'REVEAL', time: t, design: 'classic', color: '#111', y_pct: 48, fontSize: 11, width: 70, bold: true, backgroundColor: 'rgba(255,255,255,0.95)', animation: 'wipe-up', anim_duration: 0.6, animation_out: 'wipe-down' })],
  },
];

export const TEXT_TEMPLATE_CATEGORIES: Array<{ key: TextTemplateCategory; label: string }> = [
  { key: 'title', label: 'Titles' },
  { key: 'hook', label: 'Hooks' },
  { key: 'lower-third', label: 'Lower Thirds' },
  { key: 'quote', label: 'Quotes' },
  { key: 'cta', label: 'Call to Action' },
  { key: 'list', label: 'Lists & Steps' },
  { key: 'animated', label: 'Animated' },
];
